import { unitStats } from "./data.js";
import { UNIT_KINDS, type AttackPlan, type UnitKind, type ValidationResult } from "./types.js";

/**
 * What an attacking army is made of, and whether the attacker actually has it.
 *
 * This is deliberately NOT part of `validatePlan`. That function enforces the
 * simulation's own limits — how many units and waves one battle may contain —
 * and the golden scenarios push hard against them on purpose (020 sends the
 * full 200 units). Folding "do you own these" in there would make those
 * scenarios illegal and would tie a question about one player's village to the
 * permanent rulebook.
 *
 * Until D-109 the question here was "can you pay for this army?", against a
 * flat budget, because Phase 0 had no economy and no training and an army had
 * to come from somewhere (D-094). It does now: units are trained, they stand in
 * a village eating grain, and an attack sends the ones that are there. So the
 * question is no longer what the army costs but whether it exists — which is
 * what makes a raid a decision about an army you had to build rather than a
 * shopping trip with a fresh budget every time.
 */

/** How many of each kind a plan sends. Absent means none. */
export type UnitCounts = Readonly<Partial<Record<UnitKind, number>>>;

/** The plan, counted by unit kind — across every wave. */
export function planUnits(plan: AttackPlan): UnitCounts {
  const counts: Partial<Record<UnitKind, number>> = {};
  for (const wave of plan.waves) {
    for (const kind of wave.units) counts[kind] = (counts[kind] ?? 0) + 1;
  }
  return counts;
}

/**
 * What an army is worth, as one number.
 *
 * No longer a rule — nothing refuses an attack over this — but still the right
 * measure for "are these two armies comparable?", which is what the balance
 * sweep and the editor's presets need. `totalCost` comes straight off the Units
 * sheet rather than being re-added here.
 */
export function planCost(plan: AttackPlan): number {
  let total = 0;
  for (const wave of plan.waves) {
    for (const kind of wave.units) total += unitStats(kind).totalCost;
  }
  return total;
}

/** How many units a plan sends. */
export function planSize(plan: AttackPlan): number {
  let total = 0;
  for (const wave of plan.waves) total += wave.units.length;
  return total;
}

/**
 * Does the attacker actually have this army?
 *
 * Takes the held counts as an argument rather than reaching for a village,
 * because core-sim does not know what a village is — the server passes what it
 * read under a lock, and the client passes what the last economy answer said,
 * and both ask exactly the same question.
 *
 * The message names ONE unit, the first one short in sheet order, rather than
 * listing every shortfall. A player who is short three things fixes the first
 * one and asks again; a sentence with three clauses in it is read by nobody.
 */
export function checkPlanArmy(plan: AttackPlan, have: UnitCounts): ValidationResult {
  const want = planUnits(plan);
  for (const kind of UNIT_KINDS) {
    const need = want[kind] ?? 0;
    if (need === 0) continue;
    const held = have[kind] ?? 0;
    if (held >= need) continue;

    const name = unitStats(kind).name;
    return {
      ok: false,
      error: {
        code: "NOT_ENOUGH_UNITS",
        message:
          held === 0
            ? `You have no ${name}; this attack sends ${need}.`
            : `You have ${held} ${name}; this attack sends ${need}.`,
        tiles: [],
      },
    };
  }
  return { ok: true };
}
